'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { gameQueries } from '@/lib/queries'
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

type DriftedUser = {
  id: string
  email: string
  cachedBank: number
  ledgerTotal: number
  drift: number
}

function formatCredits(microdollars: number) {
  return `$${(microdollars / 1_000_000).toFixed(2)}`
}

export function ReconcileSection() {
  const [drifted, setDrifted] = useState<DriftedUser[]>([])
  const [totalUsers, setTotalUsers] = useState(0)
  const [loading, setLoading] = useState(true)
  const [fixing, setFixing] = useState<string | null>(null)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  const loadStatus = useCallback(async () => {
    setLoading(true)
    setMessage(null)
    try {
      const result = await gameQueries.getReconcileStatus()
      if (result.success) {
        setDrifted(result.drifted)
        setTotalUsers(result.totalUsers)
      }
    }
    catch (error) {
      console.error('Failed to load reconcile status:', error)
      setMessage({ type: 'error', text: `Failed to load reconcile status: ${error instanceof Error ? error.message : 'Unknown error'}` })
    }
    finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadStatus()
  }, [loadStatus])

  const handleFix = async (userId: string) => {
    setFixing(userId)
    setMessage(null)
    try {
      const result = await gameQueries.reconcileUser(userId)
      if (result.success) {
        setMessage({
          type: 'success',
          text: `Fixed: bank=${formatCredits(result.newBank)}`,
        })
        await loadStatus()
      }
    }
    catch (error) {
      console.error('Failed to reconcile user:', error)
      setMessage({ type: 'error', text: 'Failed to reconcile user' })
    }
    finally {
      setFixing(null)
    }
  }

  const handleFixAll = async () => {
    setMessage(null)
    for (const user of drifted) {
      setFixing(user.id)
      try {
        await gameQueries.reconcileUser(user.id)
      }
      catch (error) {
        console.error(`Failed to reconcile user ${user.email}:`, error)
      }
    }
    setFixing(null)
    setMessage({ type: 'success', text: `Fixed ${drifted.length} users` })
    await loadStatus()
  }

  return (
    <div className="bg-slate-50 px-4 py-8 dark:bg-slate-900 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8 text-center">
          <h2 className="mb-2 text-xl font-bold text-foreground md:text-2xl lg:text-3xl">
            Credit Reconciliation
          </h2>
          <p className="text-sm text-muted-foreground">
            Compare cached user balances against the CreditLedger and fix any drift caused by manual DB edits.
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="size-5" />
                Balance Drift Check
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={loadStatus}
                disabled={loading}
              >
                {loading
                  ? <Loader2 className="mr-2 size-4 animate-spin" />
                  : <RefreshCw className="mr-2 size-4" />}
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {message && (
              <p className={`mb-4 text-sm ${message.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-destructive'}`}>
                {message.text}
              </p>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="size-5 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Scanning users...</span>
              </div>
            ) : drifted.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-12 text-center">
                <CheckCircle2 className="size-8 text-emerald-500" />
                <p className="text-lg font-medium text-emerald-600 dark:text-emerald-400">
                  All balances are in sync
                </p>
                <p className="text-sm text-muted-foreground">
                  Scanned {totalUsers} users — no drift detected.
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="mb-3 flex items-center justify-between rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="size-5 text-amber-500" />
                    <span className="text-sm text-amber-700 dark:text-amber-300">
                      {drifted.length} user{drifted.length > 1 ? 's' : ''} with balance drift detected out of {totalUsers} users
                    </span>
                  </div>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={handleFixAll}
                    disabled={!!fixing}
                  >
                    Fix All
                  </Button>
                </div>

                {/* Header */}
                <div className="grid grid-cols-[1fr_100px_100px_100px_70px] items-center gap-2 border-b px-2 pb-2 text-xs font-medium text-muted-foreground">
                  <span>User</span>
                  <span className="text-right">Cached Bank</span>
                  <span className="text-right">Ledger Total</span>
                  <span className="text-right">Drift</span>
                  <span className="text-center">Action</span>
                </div>

                {drifted.map(u => (
                  <div
                    key={u.id}
                    className="grid grid-cols-[1fr_100px_100px_100px_70px] items-center gap-2 rounded-md px-2 py-2.5 transition-colors hover:bg-muted/50"
                  >
                    <div className="min-w-0">
                      <span className="truncate text-sm font-medium">{u.email}</span>
                    </div>
                    <span className="text-right text-sm tabular-nums">{formatCredits(u.cachedBank)}</span>
                    <span className="text-right text-sm tabular-nums text-blue-500">{formatCredits(u.ledgerTotal)}</span>
                    <span className={`text-right text-sm font-medium tabular-nums ${u.drift !== 0 ? 'text-red-500' : ''}`}>
                      {u.drift !== 0 ? formatCredits(u.drift) : '—'}
                    </span>
                    <div className="flex justify-center">
                      <Button
                        size="sm"
                        variant="destructive"
                        className="h-7 px-2 text-xs"
                        onClick={() => handleFix(u.id)}
                        disabled={!!fixing}
                      >
                        {fixing === u.id ? <Loader2 className="size-3 animate-spin" /> : 'Fix'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
